import React from 'react';
import { C, radius, shadow } from '../styles';
import { useJobs } from '../context/JobsContext';

const STEPS = [
  'Loading file', 'Column filter', 'Deduplication',
  'GSTIN validation', 'Error flags', 'Generating Excel report',
];

function StatusDot({ status }) {
  const map = {
    processing: { bg: C.accent,  anim: 'pulse 1.2s infinite' },
    completed:  { bg: C.success, anim: 'none' },
    failed:     { bg: C.danger,  anim: 'none' },
    Queued:     { bg: C.warn,    anim: 'pulse 1.2s infinite' },
  };
  const s = map[status] || map.Queued;
  return (
    <span style={{
      display:'inline-block', width:8, height:8, borderRadius:'50%',
      background: s.bg, animation: s.anim, flexShrink:0,
      boxShadow: `0 0 0 3px ${s.bg}22`,
    }} />
  );
}

export default function JobCard({ job, compact = false, onOpen }) {
  const { API, dismissJob } = useJobs();
  const isProcessing = job.status === 'processing';
  const isDone       = job.status === 'completed';
  const isFailed     = job.status === 'failed';

  const stepIdx = STEPS.findIndex(s =>
    job.current_step?.toLowerCase().includes(s.toLowerCase())
  );
  const progress = isDone ? 100 :
    isFailed ? 0 :
    stepIdx >= 0 ? Math.round(((stepIdx + 1) / STEPS.length) * 90) : 5;

  const statusColor = isDone ? C.success : isFailed ? C.danger : C.accent;

  const borderColor =
    isProcessing ? `${C.accent}55` :
    isDone       ? `${C.success}44` :
    isFailed     ? `${C.danger}44` :
    C.border2;

  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${borderColor}`,
      borderRadius: radius.md,
      padding: compact ? '12px 14px' : '16px 18px',
      animation: 'slideUp .2s ease',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: shadow.sm,
      transition: 'box-shadow .2s',
    }}>
      {/* progress bar strip at top */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
                    background: C.surface3 }}>
        <div style={{ height:'100%', width:`${progress}%`,
                      background: isDone
                        ? `linear-gradient(90deg, ${C.success}, ${C.teal})`
                        : isFailed ? C.danger
                        : `linear-gradient(90deg, ${C.accent}, ${C.teal})`,
                      transition:'width .6s ease',
                      borderRadius:'0 2px 2px 0' }} />
      </div>

      {/* header */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, marginTop:2 }}>
        <StatusDot status={job.status} />
        <span style={{ display:'flex', alignItems:'center', gap:6, flex:1, minWidth:0 }}>
          {job.report_type && (
            <span style={{
              fontSize:10, fontWeight:700, letterSpacing:'.05em',
              color: job.report_type === '2b' ? C.accent2 : C.success,
              background: job.report_type === '2b' ? C.accentBg : C.successBg,
              border: `1px solid ${job.report_type === '2b' ? C.accent + '33' : C.success + '33'}`,
              padding:'2px 7px', borderRadius:20, flexShrink:0,
            }}>{job.report_type.toUpperCase()}</span>
          )}
          <span style={{ fontFamily:'Inter,sans-serif', fontWeight:600, fontSize:13,
                         color: C.text, overflow:'hidden', textOverflow:'ellipsis',
                         whiteSpace:'nowrap' }}>
            {job.filename?.split('.')[0]?.slice(0,28) || 'GST Run'}
          </span>
        </span>
        <span style={{ fontSize:11, color: statusColor, fontWeight:600, flexShrink:0 }}>
          {isDone ? 'Complete' : isFailed ? 'Failed' : job.current_step || 'Queued'}
        </span>
        <button onClick={() => dismissJob(job.run_id)} title="Dismiss" style={{
          background:'none', border:'none', color: C.mutedSoft, cursor:'pointer',
          fontSize:18, lineHeight:1, padding:'0 2px', flexShrink:0,
        }}
        onMouseEnter={e => e.currentTarget.style.color = C.danger}
        onMouseLeave={e => e.currentTarget.style.color = C.mutedSoft}
        >×</button>
      </div>

      <div style={{ fontSize:11, color: C.muted, marginBottom: compact ? 0 : 10,
                    fontFamily:'DM Mono,monospace' }}>
        {(job.filename || '').slice(0, 36)}
        {job.filename && job.filename.length > 36 ? '…' : ''}
        &nbsp;·&nbsp; {job.run_id.slice(-8)}
      </div>

      {!compact && (
        <>
          {/* step pills */}
          <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:10 }}>
            {STEPS.map((s, i) => {
              const done   = isDone || i < stepIdx;
              const active = !isDone && i === stepIdx;
              return (
                <span key={s} style={{
                  fontSize:10, padding:'3px 8px', borderRadius:20,
                  background: done ? C.successBg : active ? C.accentBg : C.surface3,
                  color:      done ? C.success   : active ? C.accent   : C.mutedSoft,
                  border:`1px solid ${done ? C.success+'33' : active ? C.accent+'44' : 'transparent'}`,
                  animation: active ? 'pulse 1.2s infinite' : 'none',
                  fontWeight:500,
                }}>
                  {done ? '✓ ' : active ? '● ' : ''}{s}
                </span>
              );
            })}
          </div>

          {/* stats row when done */}
          {isDone && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6, marginBottom:10 }}>
              {[
                ['Total',   job.original_count, C.accent],
                ['Clean',   job.clean_count,    C.success],
                ['Flagged', job.flagged_count,  C.danger],
                ['Removed', job.removed_count,  C.warn],
              ].map(([label, val, color]) => (
                <div key={label} style={{ background: C.surface2,
                                          border:`1px solid ${C.border}`,
                                          borderRadius:6, padding:'7px 8px', textAlign:'center' }}>
                  <div style={{ fontSize:10, color: C.muted, marginBottom:3 }}>{label}</div>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:14,
                                fontWeight:600, color }}>{(val||0).toLocaleString('en-IN')}</div>
                </div>
              ))}
            </div>
          )}

          {/* error badge summary */}
          {isDone && job.flag_counts && Object.values(job.flag_counts).some(c => c > 0) && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:10 }}>
              {Object.entries(job.flag_counts)
                .filter(([,c]) => c > 0)
                .sort((a,b) => b[1]-a[1])
                .slice(0,5)
                .map(([flag, count]) => (
                  <span key={flag} style={{
                    fontSize:10, padding:'2px 7px', borderRadius:4,
                    background: C.dangerBg, color: C.danger,
                    border:`1px solid ${C.danger}22`, fontWeight:500,
                  }}>
                    {flag.replace('FLAG_','').replace(/_/g,' ')}: {count.toLocaleString('en-IN')}
                  </span>
                ))}
            </div>
          )}

          {isFailed && (
            <div style={{ fontSize:11, color: C.danger, background: C.dangerBg,
                          border:`1px solid ${C.danger}22`,
                          borderRadius:6, padding:'7px 10px', marginBottom:8 }}>
              {job.error || 'Processing failed'}
            </div>
          )}

          {/* action buttons */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {isDone && job.output_file && (
              <a href={`${API}/download/${job.output_file}`}
                 download style={{ textDecoration:'none' }}>
                <button style={{
                  background: `linear-gradient(135deg, ${C.accent}, ${C.teal})`,
                  border:'none', borderRadius:6,
                  padding:'7px 14px', color:'#fff', fontSize:12,
                  fontWeight:600, cursor:'pointer', fontFamily:'Inter,sans-serif',
                  boxShadow:'0 2px 6px rgba(8,145,178,0.25)',
                }}>⬇ Download Report (.xlsx)</button>
              </a>
            )}
            {isDone && job.large_file && (
              <span style={{ fontSize:11, color: C.warn, alignSelf:'center',
                             background: C.warnBg, border:`1px solid ${C.warn}33`,
                             padding:'4px 9px', borderRadius:4 }}>
                Large file — Excel shows first 20K rows
              </span>
            )}
            {isDone && (job.companion_files||[]).map(cf => (
              <a key={cf} href={`${API}/download/${cf}`}
                 download style={{ textDecoration:'none' }}>
                <button style={{
                  border:`1px solid ${C.success}44`,
                  borderRadius:6, padding:'7px 12px', color: C.success,
                  fontSize:11, cursor:'pointer', background: C.successBg,
                  fontWeight:600,
                }}>
                  ⬇ {cf.includes('flagged') ? 'Full Flagged CSV' : 'Full Data CSV'}
                </button>
              </a>
            ))}
            {onOpen && isDone && (
              <button onClick={() => onOpen(job)} style={{
                background: C.surface, border:`1px solid ${C.border2}`,
                borderRadius:6, padding:'7px 12px', color: C.textSoft,
                fontSize:12, cursor:'pointer', fontWeight:500,
              }}>View Details</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}